/**
 * Типизированный клиент API. Держит JWT в памяти (не в localStorage: токен
 * живёт 24 часа и легко переполучается по initData), на 401 один раз
 * переавторизуется и повторяет запрос.
 */
import type { BillingSummary, PaymentProvider } from '@reels/shared/billing';
import type { ProjectSettings } from '@reels/shared';
import { initDataRaw } from './telegram';
import { getLang, t } from '../i18n';
import type { Lang } from '../i18n';
import type {
  AuthResponse,
  AuthUser,
  CompleteAssetResponse,
  CompletedPart,
  CreateAssetRequest,
  CreateAssetResponse,
  EdlPatch,
  EdlPatchResponse,
  MeResponse,
  OkResponse,
  Project,
  ProjectDetail,
  ProjectFormat,
  ProjectListResponse,
} from './types';

export type ApiErrorCode =
  | 'bad_request'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'project_busy'
  | 'file_too_large'
  | 'invalid_settings'
  | 'rate_limited'
  | 'insufficient_balance'
  | 'server_error'
  | 'offline'
  | 'network'
  | 'aborted';

/** Человеческие тексты по кодам ошибок контракта. */
function errorText(code: ApiErrorCode): string {
  return t(`api.error.${code}`);
}

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;

  constructor(code: ApiErrorCode, message?: string, status = 0) {
    super(message && message.length > 0 ? message : errorText(code));
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

function codeFromStatus(status: number, serverCode?: string): ApiErrorCode {
  const known: ApiErrorCode[] = [
    'bad_request',
    'unauthorized',
    'forbidden',
    'not_found',
    'project_busy',
    'file_too_large',
    'invalid_settings',
    'rate_limited',
    'insufficient_balance',
    'server_error',
  ];
  if (serverCode && (known as string[]).includes(serverCode)) return serverCode as ApiErrorCode;
  switch (status) {
    case 400:
      return 'bad_request';
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    case 404:
      return 'not_found';
    case 409:
      return 'project_busy';
    case 413:
      return 'file_too_large';
    case 422:
      return 'invalid_settings';
    case 429:
      return 'rate_limited';
    default:
      return 'server_error';
  }
}

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface TransportRequest {
  method: HttpMethod;
  path: string;
  body?: unknown;
  /** Запросы без авторизации: /auth/telegram и /health. */
  anonymous?: boolean;
  signal?: AbortSignal;
}

export type Transport = (request: TransportRequest) => Promise<unknown>;

const rawBase = (import.meta.env['VITE_API_URL'] as string | undefined) ?? `${import.meta.env.BASE_URL}api`;
export const apiBaseUrl: string = rawBase.replace(/\/+$/, '');

let token: string | null = null;
let user: AuthUser | null = null;
let authPromise: Promise<AuthResponse> | null = null;

export function authToken(): string | null {
  return token;
}

export function currentUser(): AuthUser | null {
  return user;
}

/** Демо-режим: приложение открыто в браузере без Telegram, ходить некуда. */
let demo = false;

export function isDemo(): boolean {
  return demo;
}

let transport: Transport = httpTransport;

export function useTransport(next: Transport, demoMode: boolean): void {
  transport = next;
  demo = demoMode;
}

async function httpTransport(request: TransportRequest): Promise<unknown> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw new ApiError('offline');
  }
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (request.body !== undefined) headers['Content-Type'] = 'application/json';
  if (!request.anonymous && token) headers['Authorization'] = `Bearer ${token}`;

  let response: Response;
  try {
    const init: RequestInit = {
      method: request.method,
      headers,
      ...(request.body === undefined ? {} : { body: JSON.stringify(request.body) }),
      ...(request.signal ? { signal: request.signal } : {}),
    };
    response = await fetch(`${apiBaseUrl}${request.path}`, init);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw new ApiError('aborted');
    throw new ApiError('network');
  }

  if (response.status === 204) return {};

  const text = await response.text();
  let payload: unknown = undefined;
  if (text.length > 0) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = undefined;
    }
  }

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string } } | undefined)?.error;
    throw new ApiError(codeFromStatus(response.status, error?.code), error?.message, response.status);
  }
  return payload ?? {};
}

async function call<T>(request: TransportRequest): Promise<T> {
  try {
    return (await transport(request)) as T;
  } catch (error) {
    const isAuthProblem = error instanceof ApiError && error.code === 'unauthorized';
    if (!isAuthProblem || request.anonymous || !initDataRaw) throw error;
    // Один повтор: токен протух, меняем его и пробуем ещё раз.
    token = null;
    authPromise = null;
    await authenticate();
    return (await transport(request)) as T;
  }
}

export async function authenticate(): Promise<AuthResponse> {
  if (token && user) return { token, user };
  if (!authPromise) {
    const payload = { initData: initDataRaw ?? '', lang: getLang() };
    authPromise = call<AuthResponse>({
      method: 'POST',
      path: '/auth/telegram',
      body: payload,
      anonymous: true,
    })
      .then((result) => {
        token = result.token;
        user = result.user;
        return result;
      })
      .catch((error: unknown) => {
        authPromise = null;
        throw error;
      });
  }
  return authPromise;
}

export const api = {
  authenticate,
  billing(): Promise<BillingSummary> {
    return call({ method: 'GET', path: '/billing' });
  },
  cancelSubscription(): Promise<BillingSummary> {
    return call({ method: 'POST', path: '/billing/subscription/cancel', body: {} });
  },
  checkout(packageId: string, provider: PaymentProvider): Promise<{ provider: PaymentProvider; url: string; orderId?: string }> {
    return call({ method: 'POST', path: '/billing/checkout', body: { packageId, provider, acceptedTerms: true } });
  },

  redeemPromo(code: string): Promise<{ videos: number; billing: BillingSummary }> {
    return call({ method: 'POST', path: '/promo/redeem', body: { code } });
  },

  requestReferralWithdrawal(): Promise<unknown> {
    return call({ method: 'POST', path: '/referrals/withdrawals', body: {} });
  },

  /** Профиль: счётчики генераций и ссылки поддержки. */
  me(): Promise<MeResponse> {
    return call<MeResponse>({ method: 'GET', path: '/me' });
  },

  /** Онбординг просмотрен: запоминается у пользователя, а не в WebView. */
  markOnboardingSeen(): Promise<OkResponse> {
    return call<OkResponse>({ method: 'PATCH', path: '/me', body: { onboardingSeen: true } });
  },

  /** Язык интерфейса запоминается на сервере: на нём бот пишет в чат. */
  setLanguage(lang: Lang): Promise<OkResponse> {
    return call<OkResponse>({ method: 'PATCH', path: '/me', body: { lang } });
  },

  createProject(format: ProjectFormat): Promise<Project> {
    return call<Project>({ method: 'POST', path: '/projects', body: { format } });
  },

  listProjects(signal?: AbortSignal): Promise<ProjectListResponse> {
    return call<ProjectListResponse>({
      method: 'GET',
      path: '/projects',
      ...(signal ? { signal } : {}),
    });
  },

  getProject(id: string, signal?: AbortSignal): Promise<ProjectDetail> {
    return call<ProjectDetail>({
      method: 'GET',
      path: `/projects/${id}`,
      ...(signal ? { signal } : {}),
    });
  },

  renameProject(id: string, title: string): Promise<Project> {
    return call<Project>({ method: 'PATCH', path: `/projects/${id}`, body: { title } });
  },

  deleteProject(id: string): Promise<OkResponse> {
    return call<OkResponse>({ method: 'DELETE', path: `/projects/${id}` });
  },

  updateSettings(id: string, settings: ProjectSettings): Promise<Project> {
    return call<Project>({ method: 'PATCH', path: `/projects/${id}/settings`, body: settings });
  },

  generate(id: string): Promise<OkResponse> {
    return call<OkResponse>({ method: 'POST', path: `/projects/${id}/generate` });
  },

  patchEdl(id: string, patch: EdlPatch): Promise<EdlPatchResponse> {
    return call<EdlPatchResponse>({ method: 'PATCH', path: `/projects/${id}/edl`, body: patch });
  },

  /** Новый монтаж готового проекта: заново расшифровка (если устарела), план и сборка. */
  remontage(id: string): Promise<OkResponse> {
    return call<OkResponse>({ method: 'POST', path: `/projects/${id}/remontage` });
  },

  rerender(id: string, expectedVersion: number): Promise<OkResponse> {
    return call<OkResponse>({ method: 'POST', path: `/projects/${id}/rerender`, body: { expectedVersion } });
  },

  deliver(id: string): Promise<OkResponse> {
    return call<OkResponse>({ method: 'POST', path: `/projects/${id}/deliver` });
  },

  reorderSources(projectId: string, assetIds: string[]): Promise<OkResponse> {
    return call<OkResponse>({ method: 'PUT', path: `/projects/${projectId}/sources/order`, body: { assetIds } });
  },

  refreshUpload(assetId: string): Promise<{ complete: boolean; parts?: import('./types').UploadPartUrl[]; asset?: import('./types').ProjectAsset }> {
    return call({ method: 'POST', path: `/assets/${assetId}/refresh` });
  },

  createAsset(projectId: string, body: CreateAssetRequest): Promise<CreateAssetResponse> {
    return call<CreateAssetResponse>({ method: 'POST', path: `/projects/${projectId}/assets`, body });
  },

  completeAsset(assetId: string, parts: CompletedPart[]): Promise<CompleteAssetResponse> {
    return call<CompleteAssetResponse>({
      method: 'POST',
      path: `/assets/${assetId}/complete`,
      body: { parts },
    });
  },

  deleteAsset(assetId: string): Promise<OkResponse> {
    return call<OkResponse>({ method: 'DELETE', path: `/assets/${assetId}` });
  },
};

/** Текст ошибки на русском для любого исключения. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message.length > 0) return error.message;
  return t('api.error.unknown');
}
